import {useSyncExternalStore} from 'react'

// localStorage-canonical, synchronous consent state for optional tracking (Google Analytics +
// Sentry today). Read on every app boot before any tracker fires, so we can gate `initSentry`
// without a round-trip to the backend. For logged-in users, see ./sync.ts which mirrors this
// state into the DB via PATCH /auth/me/cookie-consent (separate concern, this file stays
// network-free so it can run during module init).

type AnalyticsConsentSource = 'banner' | 'cookies_page' | 'dnt'

interface ConsentState {
  analytics: boolean
  /** Client-side timestamp (Date.now()) of the choice. Used by sync.ts conflict resolution. */
  ts: number
  source: AnalyticsConsentSource
}

const LS_KEY = 'ecr.cookie.consent'
const CONSENT_CHANGED_EVENT = 'ecr-consent-changed'

let cached: ConsentState | null = null
let dntFlash = false
type Listener = () => void
const listeners = new Set<Listener>()

const isBrowser = typeof window !== 'undefined'

const readLS = (): ConsentState | null => {
  if (!isBrowser) return null
  try {
    const raw = window.localStorage.getItem(LS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ConsentState>
    if (typeof parsed.analytics !== 'boolean' || typeof parsed.ts !== 'number') return null
    const source = parsed.source ?? 'banner'
    if (source !== 'banner' && source !== 'cookies_page' && source !== 'dnt') return null
    return {analytics: parsed.analytics, ts: parsed.ts, source}
  } catch {
    return null
  }
}

const writeLS = (state: ConsentState): void => {
  if (!isBrowser) return
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(state))
  } catch {
    // Storage may be disabled (e.g. partitioned iframe); fall through with in-memory state.
  }
}

const detectDnt = (): boolean => {
  if (!isBrowser) return false
  const nav = window.navigator as Navigator & {globalPrivacyControl?: boolean}
  return nav.doNotTrack === '1' || nav.globalPrivacyControl === true
}

const notifyListeners = (): void => {
  for (const l of listeners) l()
}

const bootstrap = (): void => {
  if (!isBrowser) return
  const fromLs = readLS()
  if (fromLs) {
    cached = fromLs
    return
  }
  if (detectDnt()) {
    // Pre-set to rejected per the user's browser-level signal, but mark a flash flag so the
    // banner can render an informational "we detected DNT" notice instead of staying hidden.
    // The consent is still stored as if the user picked Reject from the source 'dnt'.
    cached = {analytics: false, ts: Date.now(), source: 'dnt'}
    dntFlash = true
    writeLS(cached)
  }
}

bootstrap()

if (isBrowser) {
  // Cross-tab sync: any other tab writing the LS key updates this cache.
  window.addEventListener('storage', (e) => {
    if (e.key !== LS_KEY) return
    const next = readLS()
    if (next === null && cached === null) return
    cached = next
    notifyListeners()
  })
}

const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getConsent = (): ConsentState | null => cached

const setConsent = (analytics: boolean, source: AnalyticsConsentSource): void => {
  cached = {analytics, ts: Date.now(), source}
  // DNT flash is dismissed on any explicit user choice.
  if (source !== 'dnt') dntFlash = false
  writeLS(cached)
  notifyListeners()
  if (isBrowser) window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT))
}

/** Hydrate consent from a server-authoritative state (called by sync.ts reconcile). */
const hydrateFromServer = (analytics: boolean, ts: number, source: AnalyticsConsentSource): void => {
  cached = {analytics, ts, source}
  writeLS(cached)
  notifyListeners()
  if (isBrowser) window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT))
}

const dismissDntFlash = (): void => {
  dntFlash = false
  notifyListeners()
}

const isDntFlashActive = (): boolean => dntFlash

const useConsent = (): ConsentState | null => useSyncExternalStore(subscribe, getConsent, () => null)

// Separate subscription for the DNT flash flag. useConsent only re-renders when `cached`
// changes identity; dismissDntFlash only flips a standalone boolean, so the banner needs its
// own hook to see that change. Both hooks share the same listener set, so a single dispatch
// reaches every subscriber.
const useDntFlash = (): boolean =>
  useSyncExternalStore(
    subscribe,
    () => dntFlash,
    () => false,
  )

export {
  getConsent,
  setConsent,
  hydrateFromServer,
  useConsent,
  useDntFlash,
  isDntFlashActive,
  dismissDntFlash,
  subscribe,
  CONSENT_CHANGED_EVENT,
  LS_KEY,
}
export type {ConsentState, AnalyticsConsentSource}
